import {AssetType} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {CheckOutlined} from '@mui/icons-material'
import {MenuItem, TextField} from '@mui/material'
import {useCallback, useRef} from 'react'
import {useTranslation} from 'react-i18next'

import {useAddAsset} from '@settings/modules/assets'
import {useConfirmDialog} from '@settings/modules/common/providers'
import {isDepartmentOrEquipmentNode} from '@settings/modules/plant-setup/helpers'
import {useSelectedItem} from '@settings/modules/plant-setup/providers'
import {useUrlParam} from '@settings/modules/routing'

export const useHandleEnableAssetForPlanning = () => {
  const {t} = useTranslation()
  const {openDialog} = useConfirmDialog()
  const plantCode = useUrlParam('plantCode')

  const {selectedItem} = useSelectedItem()

  const {mutate: addAsset, isPending: isAddingAsset} = useAddAsset(
    t('success.enabledAssetForPlanning', {name: selectedItem?.text ?? ''})
  )

  const assetTypeSelector = useRef<HTMLSelectElement>()

  const isEnableForPlanningAllowed = isDepartmentOrEquipmentNode(selectedItem)

  const handleEnableAssetForPlanning = useCallback(() => {
    if (!isEnableForPlanningAllowed) throw new Error('Enable for planning is not allowed')

    openDialog({
      title: t('plantSetup.enableForPlanningAsset'),
      description: t('plantSetup.enableForPlanningAssetDialog'),
      mainAction: {
        text: t('common.enable'),
        icon: <CheckOutlined />,
        onAction: () => {
          addAsset({
            plantCode,
            name: selectedItem.text ?? '',
            type: assetTypeSelector.current?.value as AssetType,
            upmId: selectedItem.upmId
          })
        }
      },
      additionalContent: (
        <TextField
          inputRef={assetTypeSelector}
          variant="outlined"
          fullWidth
          select
          label={t('assetsSettings.assetType')}
          defaultValue={AssetType.CementMill}
          {...dataTestId('plant-structure-details-asset-type-selector')}
        >
          {Object.values(AssetType)
            .filter((type) => type !== AssetType.BaseLoad)
            .map((type) => (
              <MenuItem
                key={type}
                value={type}
                {...dataTestId(`plant-structure-details-asset-type-selector-${type}`)}
              >
                {t(`assetsSettings.assetTypes.${type}`)}
              </MenuItem>
            ))}
        </TextField>
      )
    })
  }, [
    isEnableForPlanningAllowed,
    selectedItem?.text,
    selectedItem?.upmId,
    plantCode,
    t,
    openDialog,
    addAsset
  ])

  return {
    isAddingAsset,
    handleEnableAssetForPlanning
  }
}
